import React, { useState, useEffect, useRef } from 'react';
import './SummaryEditor.css';

/**
 * SummaryEditor - A component for editing document summaries with rich text annotations.
 * Uses a contentEditable div for WYSIWYG editing with colored annotations.
 * 
 * @param {Object} props
 * @param {string} props.value - The current summary text (HTML string)
 * @param {string} props.originalSummary - The original AI-generated summary (read-only)
 * @param {string} props.editedSummary - The edited summary with annotations (read-only)
 * @param {string} props.customSummary - The custom prompt-generated summary (read-only)
 * @param {Function} props.onChange - Callback when summary changes
 * @param {Function} props.onViewChange - Callback when view mode changes (viewMode, summary)
 * @param {boolean} props.disabled - Whether the editor is disabled
 * @param {boolean} props.externalEditTrigger - External control to trigger edit mode
 * @param {Function} props.onEditModeChange - Callback when edit mode changes
 */

// Matches real HTML tags only; a bare "<" (e.g. "<1%" in a table cell) must not count as markup
const HTML_TAG_REGEX = /<\/?[a-z][a-z0-9]*(\s[^<>\n]*)?\/?>/i;

/**
 * Unescape markdown punctuation that models often emit as literals.
 * Fill-in blanks come through as \_\_\_\_ instead of ____.
 *
 * @param {string} text - Markdown or plain text
 * @returns {string} Text with escaped underscores restored
 */
const unescapeMarkdownPunctuation = (text) => {
  if (!text) return text;
  return text.replace(/\\_/g, '_');
};

// Helper function to convert plain text to HTML (preserves line breaks and basic formatting)
const textToHtml = (text) => {
  if (!text) return '';

  // If it already looks like HTML (contains tags), return as is
  if (HTML_TAG_REGEX.test(text)) {
    return text;
  }

  // Convert plain text/markdown to HTML
  let html = unescapeMarkdownPunctuation(text);

  // Process markdown tables FIRST (before any other processing)
  // Match pipe-delimited tables
  const tableRegex = /(\|.+\|[\r\n]+)+/g;
  const tables = [];
  let tableIndex = 0;
  
  html = html.replace(tableRegex, (tableMatch) => {
    const rows = tableMatch.trim().split(/[\r\n]+/).filter(row => row.trim());
    if (rows.length < 2) return tableMatch; // Need at least header and separator
    
    // Check if second row is separator (contains dashes and pipes)
    if (!/^\|[\s-:|]+\|$/.test(rows[1].trim())) return tableMatch;
    
    // Helper function to process markdown within a cell
    let cellSourceCounter = 0;
    const processCellMarkdown = (cell) => {
      let processed = unescapeMarkdownPunctuation(cell.trim());
      
      // Links: [text](url) - check for citation sources
      processed = processed.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, linkText, url) => {
        // Check if URL has a #:~:text= fragment (text fragment identifier - likely a source)
        const textFragmentMatch = url.match(/#:~:text=([^&]+)/);
        if (textFragmentMatch) {
          // This is a citation source
          cellSourceCounter++;
          return `${linkText} Source: [<a href="${url}" target="_blank" rel="noopener noreferrer">${cellSourceCounter}</a>]`;
        }
        // Regular link - use the original link text
        return `<a href="${url}" target="_blank" rel="noopener noreferrer">${linkText}</a>`;
      });
      
      // Bold: **text**
      processed = processed.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      
      // Italic: *text* (but not if it's part of **)
      processed = processed.replace(/(?<!\*)\*([^*]+?)\*(?!\*)/g, '<em>$1</em>');
      
      // Code: `text`
      processed = processed.replace(/`([^`]+)`/g, '<code>$1</code>');
      
      return processed;
    };
    
    // Parse table
    let tableHtml = '<table>';
    
    // Header row
    const headerCells = rows[0].split('|').filter(cell => cell.trim()).map(cell => processCellMarkdown(cell));
    tableHtml += '<thead><tr>';
    headerCells.forEach(cell => {
      tableHtml += `<th>${cell}</th>`;
    });
    tableHtml += '</tr></thead>';
    
    // Body rows (skip separator row at index 1)
    if (rows.length > 2) {
      tableHtml += '<tbody>';
      for (let i = 2; i < rows.length; i++) {
        const cells = rows[i].split('|').filter(cell => cell.trim()).map(cell => processCellMarkdown(cell));
        tableHtml += '<tr>';
        cells.forEach(cell => {
          tableHtml += `<td>${cell}</td>`;
        });
        tableHtml += '</tr>';
      }
      tableHtml += '</tbody>';
    }
    
    tableHtml += '</table>';
    
    // Store table and replace with placeholder
    const placeholder = `__TABLE_${tableIndex}__`;
    tables.push({ placeholder, html: tableHtml });
    tableIndex++;
    // Ensure placeholder is surrounded by newlines for proper markdown processing
    return `\n${placeholder}\n`;
  });

  // Process headings BEFORE converting line breaks (process from largest to smallest)
  html = html.replace(/^#{6}\s+(.+?)$/gm, '<h6>$1</h6>');
  html = html.replace(/^#{5}\s+(.+?)$/gm, '<h5>$1</h5>');
  html = html.replace(/^#{4}\s+(.+?)$/gm, '<h4>$1</h4>');
  html = html.replace(/^#{3}\s+(.+?)$/gm, '<h3>$1</h3>');
  html = html.replace(/^#{2}\s+(.+?)$/gm, '<h2>$1</h2>');
  html = html.replace(/^#{1}\s+(.+?)$/gm, '<h1>$1</h1>');

  // Numbered lists: 1. item, 2. item (before line breaks)
  html = html.replace(/^\d+\.\s+(.+?)$/gm, '<oli>$1</oli>');

  // Bullet points: - item (before line breaks)
  html = html.replace(/^- (.+?)$/gm, '<li>$1</li>');

  // Links: [text](url) - markdown style links
  // Check if this is a citation source (has #:~:text= fragment)
  let sourceCounter = 0;
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, linkText, url) => {
    // Check if URL has a #:~:text= fragment (text fragment identifier - likely a source)
    const textFragmentMatch = url.match(/#:~:text=([^&]+)/);
    if (textFragmentMatch) {
      // This is a citation source
      sourceCounter++;
      return `${linkText} Source: [<a href="${url}" target="_blank" rel="noopener noreferrer">${sourceCounter}</a>]`;
    }
    // Regular link - use the original link text
    return `<a href="${url}" target="_blank" rel="noopener noreferrer">${linkText}</a>`;
  });

  // Auto-link plain URLs (http, https, ftp)
  // This regex finds URLs that aren't already in href attributes
  html = html.replace(/(?<!href=["'])(?<!src=["'])(https?:\/\/[^\s<>"{}|\\^`\[\]]+)/gi, (match) => {
    // Extract a cleaner link text from the URL
    try {
      const url = new URL(match);
      // Use domain + path (without query strings or fragments)
      let linkText = url.hostname + url.pathname;
      // Remove trailing slash
      if (linkText.endsWith('/')) {
        linkText = linkText.slice(0, -1);
      }
      // If path is empty, just use domain
      if (linkText === url.hostname + '/') {
        linkText = url.hostname;
      }
      return `<a href="${match}" target="_blank" rel="noopener noreferrer">${linkText}</a>`;
    } catch (e) {
      // If URL parsing fails, just use the match as-is
      return `<a href="${match}" target="_blank" rel="noopener noreferrer">${match}</a>`;
    }
  });

  // Bold: **text**
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

  // Italic: *text*
  html = html.replace(/\*([^*]+?)\*/g, '<em>$1</em>');

  // Convert line breaks more intelligently:
  // - Multiple line breaks become single line break
  // - Single line breaks after headings or list items are removed
  // - Other single line breaks become <br>

  // First, convert multiple line breaks to single line break
  html = html.replace(/\n\n+/g, '\n');

  // Remove line breaks after heading or list item tags
  html = html.replace(/(<\/h[1-6]>)\n/g, '$1');
  html = html.replace(/(<\/li>)\n/g, '$1');
  html = html.replace(/(<\/oli>)\n/g, '$1');

  // Convert remaining single line breaks to <br>
  html = html.replace(/\n/g, '<br>');

  // Wrap consecutive <li> items in <ul> tags
  html = html.replace(/(<li>.*?<\/li>)+/g, (match) => {
    return '<ul>' + match + '</ul>';
  });

  // Wrap consecutive <oli> items in <ol> tags and convert oli to li
  html = html.replace(/(<oli>.*?<\/oli>)+/g, (match) => {
    const converted = match.replace(/<\/?oli>/g, (tag) => tag.replace('oli', 'li'));
    return '<ol>' + converted + '</ol>';
  });

  // Restore tables from placeholders
  tables.forEach(({ placeholder, html: tableHtml }) => {
    html = html.replace(placeholder, tableHtml);
  });
  
  // After table restoration, ensure proper spacing around tables
  // Add line breaks before and after tables if they don't exist
  html = html.replace(/([^\n])<table>/g, '$1\n<table>');
  html = html.replace(/<\/table>([^\n])/g, '</table>\n$1');

  return html;
};

// Helper function to convert HTML back to clean markdown/text (strips annotations)
const htmlToMarkdown = (html) => {
  if (!html) return '';

  // Create a temporary div to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Remove annotation spans but keep their content
  const annotationSpans = tempDiv.querySelectorAll('span[style]');
  annotationSpans.forEach(span => {
    // For comments, extract the original text from [Comment: text] format
    if (span.style.color === 'blue' && span.style.fontStyle === 'italic') {
      const commentMatch = span.textContent.match(/\[Comment: (.+?)\]/);
      if (commentMatch) {
        span.replaceWith(document.createTextNode(commentMatch[1]));
      } else {
        span.replaceWith(document.createTextNode(span.textContent));
      }
    }
    // For corrections, keep only the original text (before the correction)
    else if (span.querySelector('span[style*="line-through"]')) {
      const strikethroughText = span.querySelector('span[style*="line-through"]').textContent;
      span.replaceWith(document.createTextNode(strikethroughText));
    }
    // For highlights and important, just keep the text content
    else {
      span.replaceWith(document.createTextNode(span.textContent));
    }
  });

  // Convert HTML elements back to markdown
  let text = tempDiv.innerHTML;

  // Convert headings
  text = text.replace(/<h([1-6])>(.+?)<\/h[1-6]>/g, (match, level, content) => {
    return '#'.repeat(parseInt(level)) + ' ' + content + '\n';
  });

  // Convert lists
  text = text.replace(/<ul>(.*?)<\/ul>/gs, (match, content) => {
    return content.replace(/<li>(.+?)<\/li>/g, '- $1\n');
  });

  text = text.replace(/<ol>(.*?)<\/ol>/gs, (match, content) => {
    let counter = 1;
    return content.replace(/<li>(.+?)<\/li>/g, (match, listItem) => {
      return `${counter++}. ${listItem}\n`;
    });
  });

  // Convert bold and italic
  text = text.replace(/<strong>(.+?)<\/strong>/g, '**$1**');
  text = text.replace(/<em>(.+?)<\/em>/g, '*$1*');

  // Convert <br> tags to line breaks
  text = text.replace(/<br\s*\/?>/g, '\n');

  // Clean up any remaining HTML tags
  text = text.replace(/<[^>]+>/g, '');

  // Clean up multiple consecutive line breaks
  text = text.replace(/\n\n+/g, '\n\n');

  // Trim whitespace
  text = text.trim();

  return text;
};

// Export the utility functions for use in other components
export { htmlToMarkdown, textToHtml };

export default function SummaryEditor({ value, originalSummary, editedSummary, customSummary, onChange, onViewChange, disabled, externalEditTrigger, onEditModeChange }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedHtml, setEditedHtml] = useState(value || '');
  const [hasChanges, setHasChanges] = useState(false);
  const [showingOriginal, setShowingOriginal] = useState(false);
  const [showingCustom, setShowingCustom] = useState(false);
  const [showTipsModal, setShowTipsModal] = useState(false);
  const [importantColor, setImportantColor] = useState('purple');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const editorRef = useRef(null);
  const [lastSelection, setLastSelection] = useState(null);
  const isUpdatingRef = useRef(false);
  const colorPickerRef = useRef(null);
  const onEditModeChangeRef = useRef(onEditModeChange);
  const onViewChangeRef = useRef(onViewChange);

  // Keep the ref up to date
  useEffect(() => {
    onEditModeChangeRef.current = onEditModeChange;
    onViewChangeRef.current = onViewChange;
  }, [onEditModeChange, onViewChange]);

  // Listen for external edit trigger
  useEffect(() => {
    if (externalEditTrigger && !isEditing) {
      setIsEditing(true);
      // onEditModeChange will be called by the next useEffect
    }
  }, [externalEditTrigger, isEditing]);

  // Notify parent when view state changes
  useEffect(() => {
    if (onEditModeChangeRef.current) {
      onEditModeChangeRef.current({ isEditing, showingOriginal });
    }
  }, [isEditing, showingOriginal]);

  // Close color picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(event.target)) {
        setShowColorPicker(false);
      }
    };

    if (showColorPicker) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showColorPicker]);

  useEffect(() => {
    // Convert text to HTML if needed
    const htmlValue = textToHtml(value);
    setEditedHtml(htmlValue);

    // Check if there are any changes from the original
    const htmlOriginal = textToHtml(originalSummary);
    // Only consider it as having changes if value exists and is different from original
    // AND if the value is not the same as originalSummary (meaning there's actually an edit)
    const hasActualChanges = htmlValue !== htmlOriginal && htmlValue !== '' && value !== originalSummary;
    setHasChanges(hasActualChanges);
  }, [value, originalSummary]);

  // Only update innerHTML when entering edit mode or content changes from outside
  useEffect(() => {
    if (isEditing && editorRef.current) {
      // Only update innerHTML if we just entered edit mode or the editor is empty
      const currentHtml = editorRef.current.innerHTML;
      if (!currentHtml || currentHtml === '' || currentHtml === '<br>') {
        editorRef.current.innerHTML = editedHtml;
      }
    }
  }, [isEditing]);

  // Update editor content when editedHtml changes from outside (e.g., when opening modal)
  useEffect(() => {
    if (isEditing && editorRef.current && !isUpdatingRef.current) {
      const currentHtml = editorRef.current.innerHTML;
      // Only update if significantly different (not just from user typing)
      if (currentHtml !== editedHtml && !currentHtml.includes(editedHtml.substring(0, 50))) {
        editorRef.current.innerHTML = editedHtml;
      }
    }
  }, [editedHtml, isEditing]);

  const handleSave = () => {
    onChange(editedHtml);
    setIsEditing(false);
    isUpdatingRef.current = false;
    // onEditModeChange will be called by useEffect
  };

  const handleCancel = () => {
    const htmlValue = textToHtml(value);
    setEditedHtml(htmlValue);
    setIsEditing(false);
    isUpdatingRef.current = false;
    // onEditModeChange will be called by useEffect
  };

  const handleRevertToOriginal = () => {
    const htmlOriginal = textToHtml(originalSummary);
    setEditedHtml(htmlOriginal);
    onChange(originalSummary || '');
    setHasChanges(false);
    isUpdatingRef.current = false;
  };

  // Save selection when user selects text
  const saveSelection = () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      setLastSelection(selection.getRangeAt(0));
    }
  };

  // Restore saved selection
  const restoreSelection = () => {
    if (lastSelection) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(lastSelection);
    }
  };

  // Apply formatting to selected text
  const applyAnnotation = (type, color = null) => {
    if (!editorRef.current) return;

    // Focus the editor and restore selection
    editorRef.current.focus();
    restoreSelection();

    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    const selectedText = range.toString().trim();

    // Check if the cursor/selection is within the editor
    const isWithinEditor = editorRef.current.contains(range.commonAncestorContainer) ||
      editorRef.current.contains(range.startContainer) ||
      editorRef.current.contains(range.endContainer);

    if (!isWithinEditor) {
      alert('Please click in the text editor first before applying an annotation.');
      return;
    }

    // For comments, if no text is selected, allow insertion at cursor position
    if (!selectedText && type !== 'comment') {
      alert('Please select some text first before applying an annotation.');
      return;
    }

    // Create wrapper element based on type
    let wrapper;
    let shouldSelectPlaceholder = false;
    switch (type) {
      case 'comment':
        wrapper = document.createElement('span');
        wrapper.style.color = 'blue';
        wrapper.style.fontStyle = 'italic';
        // If text is selected, wrap it in a comment; otherwise insert a comment placeholder
        if (selectedText) {
          wrapper.textContent = `[Comment: ${selectedText}]`;
        } else {
          wrapper.textContent = '[Comment: Add your comment here]';
          shouldSelectPlaceholder = true;
        }
        break;
      case 'correction':
        // Create a correction with strikethrough and corrected text
        const correctionContainer = document.createElement('span');
        const strikethrough = document.createElement('span');
        strikethrough.style.color = 'red';
        strikethrough.style.textDecoration = 'line-through';
        strikethrough.textContent = selectedText;
        const correctedText = document.createElement('span');
        correctedText.style.color = 'green';
        correctedText.style.marginLeft = '4px';
        correctedText.textContent = '[corrected text]';
        correctionContainer.appendChild(strikethrough);
        correctionContainer.appendChild(document.createTextNode(' '));
        correctionContainer.appendChild(correctedText);
        wrapper = correctionContainer;
        break;
      case 'highlight':
        wrapper = document.createElement('span');
        wrapper.style.backgroundColor = 'yellow';
        wrapper.style.padding = '2px 4px';
        wrapper.textContent = selectedText;
        break;
      case 'important':
        wrapper = document.createElement('span');
        wrapper.style.color = color || importantColor;
        wrapper.style.fontWeight = 'bold';
        wrapper.textContent = selectedText;
        break;
      default:
        return;
    }

    // Replace selection with wrapper
    try {
      isUpdatingRef.current = true;

      if (selectedText) {
        // Text is selected, replace it with the wrapper
        range.deleteContents();
        range.insertNode(wrapper);
      } else {
        // No text selected, insert wrapper at cursor position
        range.insertNode(wrapper);
      }

      // If we need to select the placeholder text
      if (shouldSelectPlaceholder) {
        // For comment placeholders, select "Add your comment here"
        const textNode = wrapper.firstChild;
        if (textNode && textNode.nodeType === Node.TEXT_NODE) {
          const text = textNode.textContent;
          const startIndex = text.indexOf('Add your comment here');
          if (startIndex !== -1) {
            const newRange = document.createRange();
            newRange.setStart(textNode, startIndex);
            newRange.setEnd(textNode, startIndex + 'Add your comment here'.length);
            selection.removeAllRanges();
            selection.addRange(newRange);
          }
        }
      } else {
        // Move cursor after inserted element
        range.setStartAfter(wrapper);
        range.setEndAfter(wrapper);
        selection.removeAllRanges();
        selection.addRange(range);
      }

      // Update state with current HTML
      setEditedHtml(editorRef.current.innerHTML);

      // Reset the flag after a brief delay
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 100);
    } catch (e) {
      console.error('Error applying annotation:', e);
      isUpdatingRef.current = false;
    }
  };

  return (
    <div className="summary-editor">
      {/* Header with actions */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="d-flex align-items-center">
          {/* Toggle between original, edited, and custom when not editing */}
          {(editedSummary || originalSummary || customSummary) && !isEditing && (
            <div className="btn-group btn-group-sm" role="group">
              {/* Show Edited button if there's an edited summary */}
              {editedSummary && (
                <button
                  type="button"
                  className={`btn ${!showingOriginal && !showingCustom ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => {
                    setShowingOriginal(false);
                    setShowingCustom(false);
                    if (onViewChangeRef.current) {
                      onViewChangeRef.current('edited', editedSummary);
                    }
                  }}
                >
                  <i className="bi bi-pencil-square me-1"></i>
                  Edited
                </button>
              )}
              {/* Show Original button if there's an original summary */}
              {originalSummary && (
                <button
                  type="button"
                  className={`btn ${showingOriginal ? 'btn-secondary' : 'btn-outline-secondary'}`}
                  onClick={() => {
                    setShowingOriginal(true);
                    setShowingCustom(false);
                    if (onViewChangeRef.current) {
                      onViewChangeRef.current('original', originalSummary);
                    }
                  }}
                >
                  <i className="bi bi-robot me-1"></i>
                  Original
                </button>
              )}
              {/* Show Custom button if there's a custom summary */}
              {customSummary && (
                <button
                  type="button"
                  className={`btn ${showingCustom ? 'btn-warning' : 'btn-outline-warning'}`}
                  onClick={() => {
                    setShowingOriginal(false);
                    setShowingCustom(true);
                    if (onViewChangeRef.current) {
                      onViewChangeRef.current('custom', customSummary);
                    }
                  }}
                  title="View custom summary"
                >
                  <i className="bi bi-magic me-1"></i>
                  Custom
                </button>
              )}
            </div>
          )}
        </div>
        <div className="d-flex gap-2">
          {!isEditing && hasChanges && (
            <button
              className="btn btn-sm btn-outline-warning"
              onClick={handleRevertToOriginal}
              disabled={disabled}
              title="Revert to original AI-generated summary"
            >
              <i className="bi bi-arrow-counterclockwise me-1"></i>
              Revert to Original
            </button>
          )}
        </div>
      </div>

      {/* Editor or Preview */}
      {isEditing ? (
        <div className="d-flex flex-column" style={{ height: '100%', minHeight: '400px' }}>
          {/* Annotation toolbar with Cancel/Save buttons */}
          <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
            <div className="btn-group btn-group-sm me-2" role="group">
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => applyAnnotation('comment')}
                onMouseDown={(e) => e.preventDefault()}
                title="Add a blue comment"
              >
                <i className="bi bi-chat-dots"></i> Comment
              </button>
              <button
                type="button"
                className="btn btn-outline-danger"
                onClick={() => applyAnnotation('correction')}
                onMouseDown={(e) => e.preventDefault()}
                title="Mark correction (strikethrough + green text)"
              >
                <i className="bi bi-pen"></i> Correction
              </button>
              <button
                type="button"
                className="btn btn-outline-warning"
                onClick={() => applyAnnotation('highlight')}
                onMouseDown={(e) => e.preventDefault()}
                title="Highlight text (yellow)"
              >
                <i className="bi bi-highlighter"></i> Highlight
              </button>

              {/* Important button with color picker - positioned right after the btn-group */}
              <div className="position-relative" ref={colorPickerRef}>
                <div className="btn-group btn-group-sm" role="group">
                  <button
                    type="button"
                    className="btn"
                    onClick={() => applyAnnotation('important')}
                    onMouseDown={(e) => e.preventDefault()}
                    title={`Mark as important (bold ${importantColor})`}
                    style={{
                      borderColor: importantColor,
                      color: importantColor,
                      backgroundColor: 'transparent',
                      borderRadius: 0
                    }}
                  >
                    <i className="bi bi-exclamation-triangle"></i> Important
                  </button>
                  <button
                    type="button"
                    className="btn dropdown-toggle dropdown-toggle-split"
                    onClick={() => setShowColorPicker(!showColorPicker)}
                    onMouseDown={(e) => e.preventDefault()}
                    title="Choose color"
                    style={{
                      borderColor: importantColor,
                      color: importantColor,
                      backgroundColor: 'transparent',
                      paddingLeft: '6px',
                      paddingRight: '6px',
                      borderTopRightRadius: '.25rem',
                      borderBottomRightRadius: '.25rem'
                    }}
                  >
                    <span className="visually-hidden">Toggle Dropdown</span>
                  </button>
                </div>

                {/* Color picker dropdown */}
                {showColorPicker && (
                  <div
                    className="position-absolute bg-white border rounded shadow-sm p-2"
                    style={{
                      top: '100%',
                      left: 0,
                      marginTop: '4px',
                      zIndex: 1000
                    }}
                  >
                    <div className="d-flex gap-2">
                      {[
                        { name: 'Purple', value: 'purple' },
                        { name: 'Red', value: 'red' },
                        { name: 'Orange', value: 'orange' },
                        { name: 'Blue', value: 'blue' },
                        { name: 'Green', value: 'green' },
                        { name: 'Magenta', value: 'magenta' }
                      ].map(color => (
                        <button
                          key={color.value}
                          type="button"
                          className="btn btn-sm p-0"
                          onClick={() => {
                            setImportantColor(color.value);
                            setShowColorPicker(false);
                          }}
                          onMouseDown={(e) => e.preventDefault()}
                          title={color.name}
                          style={{
                            width: '28px',
                            height: '28px',
                            backgroundColor: color.value,
                            border: importantColor === color.value ? '3px solid #0d6efd' : '2px solid #dee2e6',
                            borderRadius: '4px',
                            padding: '0'
                          }}
                        >
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            {/* Info button for tips */}
            <button
              type="button"
              className="btn btn-outline-info btn-sm"
              onClick={() => setShowTipsModal(true)}
              onMouseDown={(e) => e.preventDefault()}
              title="Show editing tips"
            >
              <i className="bi bi-info-circle"></i>
            </button>

            {/* Cancel and Save buttons - right aligned */}
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-secondary"
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                className="btn btn-sm btn-success"
                onClick={handleSave}
              >
                <i className="bi bi-check-lg me-1"></i>
                Save Changes
              </button>
            </div>
          </div>

          {/* Rich text editor (contentEditable) - scrollable */}
          <div
            ref={editorRef}
            contentEditable={true}
            suppressContentEditableWarning={true}
            className="summary-editor-content border rounded p-3"
            style={{
              flex: '1',
              fontSize: '14px',
              lineHeight: '1.6',
              backgroundColor: '#fff',
              outline: 'none',
              overflowY: 'auto',
              minHeight: '0'
            }}
            onInput={(e) => {
              if (!isUpdatingRef.current) {
                setEditedHtml(e.currentTarget.innerHTML);
              }
            }}
            onMouseUp={saveSelection}
            onKeyUp={saveSelection}
            onBlur={(e) => {
              // Save content on blur
              setEditedHtml(e.currentTarget.innerHTML);
            }}
          />
        </div>
      ) : (
        <div
          className="border rounded p-3"
          style={{
            overflow: 'auto',
            backgroundColor: '#f8f9fa',
            minHeight: '200px',
            maxHeight: '600px'
          }}
        >
          {/* Display appropriate content based on toggle and available content */}
          {(() => {
            let contentToShow;
            
            // Check if showing custom summary
            if (showingCustom && customSummary) {
              contentToShow = textToHtml(customSummary);
            }
            // Check if showing original
            else if (showingOriginal && originalSummary) {
              contentToShow = textToHtml(originalSummary);
            }
            // Check if we have edited summary to show (not showing original or custom)
            else if (!showingOriginal && !showingCustom && editedSummary) {
              contentToShow = textToHtml(editedSummary);
            }
            // Default to current value
            else {
              contentToShow = editedHtml || textToHtml(value) || textToHtml(originalSummary);
            }

            return contentToShow ? (
              <div
                className="summary-content"
                dangerouslySetInnerHTML={{ __html: contentToShow }}
                style={{
                  fontSize: '14px',
                  lineHeight: '1.6'
                }}
              />
            ) : (
              <div className="text-muted text-center" style={{ padding: '40px 0' }}>
                No summary available.
              </div>
            );
          })()}
        </div>
      )}

      {/* Tips Modal */}
      {showTipsModal && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={() => setShowTipsModal(false)}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-info-circle me-2"></i>
                  Editing Tips
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowTipsModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <h6 className="mb-3">How to Use the Summary Editor:</h6>

                <div className="mb-3">
                  <strong>Adding Annotations:</strong>
                  <ol className="mt-2 mb-0">
                    <li>Select the text you want to annotate</li>
                    <li>Click one of the annotation buttons at the top</li>
                  </ol>
                </div>

                <div className="mb-3">
                  <strong>Annotation Types:</strong>
                  <ul className="mt-2 mb-0">
                    <li><strong>Comment</strong> - Add blue italic comments</li>
                    <li><strong>Correction</strong> - Mark text with strikethrough and add green corrected text</li>
                    <li><strong>Highlight</strong> - Apply yellow background highlighting</li>
                    <li><strong>Important</strong> - Make text bold with customizable color (default: purple). Click the dropdown arrow to choose a different color.</li>
                  </ul>
                </div>

                <div className="mb-0">
                  <strong>Editing Text:</strong>
                  <p className="mb-0 mt-2">
                    You can type directly in the editor to modify the summary text, but please use the annotation buttons to make the changes clear.
                    All formatting is preserved automatically when you save.
                  </p>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setShowTipsModal(false)}
                >
                  Got it!
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
